import { createApp } from 'vue'
import 'bootstrap/dist/css/bootstrap.min.css'
// Bootstrap's JS bundle is NOT imported. The whole app contains two
// data-bs-* attributes (one popover) and uses no Bootstrap JS component,
// so the bundle was ~80KB of parse cost for nothing. If a Bootstrap
// modal/dropdown/collapse is ever needed, import it here again.
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'leaflet/dist/leaflet.css'
import '@/utils/leaflet-setup' // applies the marker-icon path fix once, globally

// Vuetify. Imported BEFORE global.css so the app's own stylesheet still has
// the last word. Vuetify 4 wraps its rules in CSS @layer, and unlayered CSS
// beats layered CSS regardless of specificity, so Bootstrap and global.css
// win every overlap by default — which is what keeps the not-yet-migrated
// screens looking exactly as they did.
import vuetify from './plugins/vuetify'

import App from './App.vue'
import { createWebHistory, createRouter } from 'vue-router'
import './assets/global.css'

import store from './store'
import { landingPath } from '@/utils/landing'
import routes from './store/router'

// Vue-toastification
import Toast, { POSITION } from "vue-toastification"
import "vue-toastification/dist/index.css"

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')
  const user = JSON.parse(localStorage.getItem('user') || 'null')
  const isSuper = !!user?.is_super_admin

  // Guest routes
  if (to.meta.guestOnly) {
    if (!token) return next()
    // Authenticated and hitting a guest page: go to their first REACHABLE
    // screen. This said next('/'), but '/' is MainDashboard and is gated, so
    // for a user without it the guard bounced the redirect and stranded them
    // on the login page.
    return next(landingPath(store))
  }

  // Protected routes need a token
  if (!token && to.meta.requiresAuth) {
    return next('/LogIn')
  }

  // Super admin bypasses permission checks
  if (token && isSuper) return next()

  // Per-user routes (e.g. your own notifications inbox) are NOT permission-gated:
  // any authenticated user may open them, regardless of route_access grants.
  // This keeps such pages out of the "new route → nobody's route_access has it yet
  // → locked out" trap that role-gated screens fall into.
  if (token && to.meta.alwaysAllow) return next()

  // Permission check — route name must appear in activeAccount.pivot.route_access
  if (token) {
    const account = JSON.parse(localStorage.getItem('activeAccount') || 'null')
    const list = account?.pivot?.route_access ?? account?.route_access ?? []
    if (to.name && Array.isArray(list) && list.includes(to.name)) return next()
    // No permission — stay where we are
    return next(false)
  }

  next()
})


const app = createApp(App)

app.use(router)
app.use(store)
app.use(vuetify)
app.use(Toast, {
  position: POSITION.TOP_RIGHT,
  timeout: 3000,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false
})

app.mount('#app')

// Register the Web Push service worker (root scope). Safe no-op on browsers
// without SW support or on non-secure origins. Subscription itself is opt-in
// via the "Enable notifications" button (see @/utils/push).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      console.warn('Service worker registration failed:', err)
    })
  })
}
